import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X } from "lucide-react";
import Terms from "@/pages/Terms";
import HandleField from "@/components/HandleField";

export default function Auth() {
  const { login, register, error, user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", handle: "" });
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [agreed, setAgreed] = useState(false);

  React.useEffect(() => { if (user) nav("/"); }, [user, nav]);

  const doRegister = async () => {
    setLoading(true);
    const ok = await register(form.name, form.email, form.password, form.phone, true, form.handle);
    setLoading(false);
    if (ok) {
      setShowTerms(false);
      toast.success("Account created — check your email for a verification link to confirm your address.");
      nav("/");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      // The Terms must pop up and be agreed to before any account is created.
      setAgreed(false);
      setShowTerms(true);
      return;
    }
    setLoading(true);
    const ok = await login(form.email, form.password);
    setLoading(false);
    if (ok) nav("/");
  };

  return (
    <div data-testid="auth-page" className="max-w-[520px] mx-auto px-6 py-16">
      <div className="label-eyebrow">Access</div>
      <h1 className="font-display text-5xl tracking-tighter mt-3">{mode === "login" ? "Welcome back." : "Join Your Classic Style."}</h1>

      <div className="mt-6 flex gap-2">
        <button className="chip" data-active={mode==="login"} onClick={()=>setMode("login")} data-testid="tab-login">Sign in</button>
        <button className="chip" data-active={mode==="register"} onClick={()=>setMode("register")} data-testid="tab-register">Create account</button>
      </div>

      <form onSubmit={submit} className="hairline mt-6 p-8 space-y-4">
        {mode === "register" && (
          <div>
            <label className="label-eyebrow">Name</label>
            <input required data-testid="input-name" className="w-full mt-2 hairline p-3 text-sm bg-white outline-none focus:border-black"
              value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
          </div>
        )}
        {mode === "register" && (
          <div>
            <label className="label-eyebrow">Handle <span className="normal-case tracking-normal text-neutral-400">(optional — shown to others instead of your name)</span></label>
            <div className="mt-2">
              <HandleField value={form.handle} onChange={(v) => setForm({ ...form, handle: v })} testidPrefix="input-handle" />
            </div>
            <p className="mt-1.5 text-xs text-neutral-500">Your real name stays private — buyers and sellers will see your handle if you set one.</p>
          </div>
        )}
        <div>
          <label className="label-eyebrow">Email</label>
          <input required type="email" data-testid="input-email" className="w-full mt-2 hairline p-3 text-sm bg-white outline-none focus:border-black"
            value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
        </div>
        {mode === "register" && (
          <div>
            <label className="label-eyebrow">Phone <span className="normal-case tracking-normal text-neutral-400">(optional)</span></label>
            <input type="tel" data-testid="input-phone" className="w-full mt-2 hairline p-3 text-sm bg-white outline-none focus:border-black"
              value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/>
          </div>
        )}
        <div>
          <label className="label-eyebrow">Password</label>
          <input required type="password" data-testid="input-password" minLength={6} className="w-full mt-2 hairline p-3 text-sm bg-white outline-none focus:border-black"
            value={form.password} onChange={e=>setForm({...form, password:e.target.value})}/>
          {mode === "login" && (
            <div className="mt-2 text-right">
              <Link to="/forgot-password" data-testid="forgot-password-link"
                className="text-xs text-neutral-500 underline hover:text-black transition-colors">
                Forgot your password?
              </Link>
            </div>
          )}
        </div>
        {error && <div data-testid="auth-error" className="text-sm text-red-600">{error}</div>}
        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="auth-submit">
          {loading ? "…" : (mode==="login" ? "Sign in" : "Create account")}
        </button>
      </form>
      <p className="mt-4 text-xs text-neutral-500">
        By continuing you agree to Your Classic Style's{" "}
        <Link className="underline" to="/terms" data-testid="auth-terms-link">Terms &amp; Conditions</Link>.{" "}
        <Link className="underline" to="/">Back home</Link>.
      </p>

      {showTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="terms-modal">
          <div className="bg-[#F9F6F0] w-full max-w-[900px] max-h-[90vh] flex flex-col hairline shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 hairline-b bg-[#F9F6F0] sticky top-0">
              <div>
                <div className="label-eyebrow">Before you join</div>
                <h2 className="font-display text-2xl tracking-tight mt-1">Our Terms &amp; Conditions</h2>
              </div>
              <button onClick={()=>setShowTerms(false)} data-testid="terms-modal-close" aria-label="Close"
                className="p-2 hover:bg-black/5 transition-colors"><X size={18}/></button>
            </div>
            <div className="overflow-y-auto flex-1" data-testid="terms-modal-body">
              <Terms />
            </div>
            <div className="px-6 py-5 hairline-t bg-[#F9F6F0] space-y-4">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-agree-row">
                <input type="checkbox" checked={agreed} onChange={e=>setAgreed(e.target.checked)}
                  data-testid="terms-agree-checkbox" className="mt-1 h-4 w-4 accent-[#1E3A2B]"/>
                <span className="text-sm text-neutral-700">I have read and agree to the Terms &amp; Conditions.</span>
              </label>
              {error && <div data-testid="terms-modal-error" className="text-sm text-red-600">{error}</div>}
              <div className="flex gap-3 justify-end">
                <button onClick={()=>setShowTerms(false)} className="btn-outline" data-testid="terms-decline-btn">Cancel</button>
                <button onClick={doRegister} disabled={!agreed || loading} className="btn-primary" data-testid="terms-accept-btn">
                  {loading ? "…" : "Agree & create account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
